import { getAuth } from "firebase/auth";

import { UserRole } from "../constants/userRoles";

const API_BASE_URL = (
  process.env.NODE_ENV === "development"
    ? process.env.REACT_APP_LOCAL_API_BASE_URL || "http://localhost:5000/api/v1"
    : "https://api.pickdengu.mw/api/v1"
).replace(/\/$/, "");

const firebaseAuth = getAuth();

export interface AdminMember {
  id: string;
  name?: string;
  displayName?: string;
  email?: string;
  phone?: string;
  photoURL?: string;
  role?: string;
  createdAt?: string | number;
  updatedAt?: string | number;
  [key: string]: unknown;
}

export interface AdminAddress {
  id: string;
  label?: string;
  addressLine?: string;
  instructions?: string;
  latitude?: number;
  longitude?: number;
  isCurrent?: boolean;
  isPickup?: boolean;
  createdAt?: string | number;
  updatedAt?: string | number;
  [key: string]: unknown;
}

export interface AdminWarehouse {
  id: string;
  name?: string;
  description?: string;
  address?: string;
  whatsappNumber?: string;
  photoURL?: string;
  lat?: number;
  lng?: number;
  isActive?: boolean;
  createdAt?: string | number;
  updatedAt?: string | number;
  [key: string]: unknown;
}

export interface AdminUserRecord {
  uid: string;
  firebaseUid?: string;
  id?: string;
  email?: string;
  displayName?: string;
  name?: string;
  photoURL?: string;
  phone?: string;
  phoneNumber?: string;
  role: UserRole | string;
  status?: "active" | "suspended" | "pending" | "deleted" | string;
  disabled?: boolean;
  emailVerified?: boolean;
  theme?: string;
  businessName?: string;
  businessAddress?: string;
  businessPhone?: string;
  businessPhoneNumber?: string;
  whatsappNumber?: string;
  logoURL?: string;
  fontColor?: string;
  accountName?: string;
  accountBank?: string;
  accountNumber?: string;
  accountBranch?: string;
  denguPoints?: number;
  totalDenguPoints?: number;
  members?: AdminMember[];
  addresses?: AdminAddress[];
  warehouses?: AdminWarehouse[];
  createdAt?: string | number | { _seconds?: number; seconds?: number };
  updatedAt?: string | number | { _seconds?: number; seconds?: number };
  [key: string]: unknown;
}

export interface AdminUsersResponse {
  users: AdminUserRecord[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface ListAdminUsersParams {
  search?: string;
  role?: string;
  status?: string;
  page?: number;
  limit?: number;
}

export interface AdminAuditRecord {
  id: string;
  actorUid: string;
  action: string;
  targetUid: string;
  reason?: string;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  createdAt?: string | number | { _seconds?: number; seconds?: number };
}

type ApiErrorPayload = {
  message?: string;
  error?: string;
  status?: string;
};

const getAuthorizationHeaders = async (
  forceRefresh = false,
): Promise<Record<string, string>> => {
  const currentUser = firebaseAuth.currentUser;

  if (!currentUser) {
    throw new Error("You must be signed in.");
  }

  const token = await currentUser.getIdToken(forceRefresh);

  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
};

const parseResponsePayload = async (
  response: Response,
): Promise<ApiErrorPayload> =>
  response.json().catch(() => ({}) as ApiErrorPayload);

const performRequest = async (
  path: string,
  init: RequestInit | undefined,
  forceRefresh: boolean,
): Promise<Response> => {
  const authorizationHeaders = await getAuthorizationHeaders(forceRefresh);

  return fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      ...authorizationHeaders,
      ...(init?.headers || {}),
    },
  });
};

const request = async <T>(path: string, init?: RequestInit): Promise<T> => {
  let response = await performRequest(path, init, false);

  /*
   * Firebase ID tokens are cached by the client SDK. When the backend reports
   * an expired, revoked, or otherwise rejected token, refresh it once and
   * retry the same request. The retry is deliberately limited to one attempt.
   */
  if (response.status === 401 && firebaseAuth.currentUser) {
    response = await performRequest(path, init, true);
  }

  const payload = await parseResponsePayload(response);

  if (!response.ok) {
    throw new Error(
      payload.message || payload.error || "The request could not be completed.",
    );
  }

  return payload as T;
};

export const adminUsersService = {
  async listUsers(params: ListAdminUsersParams = {}) {
    const query = new URLSearchParams();

    if (params.search) query.set("search", params.search);
    if (params.role && params.role !== "all") query.set("role", params.role);
    if (params.status && params.status !== "all") {
      query.set("status", params.status);
    }
    query.set("page", String(params.page || 1));
    query.set("limit", String(params.limit || 20));

    return request<AdminUsersResponse>(`/admin/users?${query.toString()}`);
  },

  async getUser(uid: string) {
    return request<{ user: AdminUserRecord }>(`/admin/users/${uid}`);
  },

  async updateUser(
    uid: string,
    changes: Partial<AdminUserRecord>,
    reason?: string,
  ) {
    return request<{ user: AdminUserRecord }>(`/admin/users/${uid}`, {
      method: "PATCH",
      body: JSON.stringify({ changes, reason }),
    });
  },

  async updateRole(uid: string, role: UserRole, reason?: string) {
    const trimmedReason = reason?.trim();

    return request<{ user: AdminUserRecord }>(`/admin/users/${uid}/role`, {
      method: "PATCH",
      body: JSON.stringify(
        trimmedReason
          ? {
              role,
              reason: trimmedReason,
            }
          : {
              role,
            },
      ),
    });
  },

  async updateStatus(
    uid: string,
    status: "active" | "suspended" | "pending" | "deleted",
    reason: string,
  ) {
    return request<{ user: AdminUserRecord }>(`/admin/users/${uid}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status, reason }),
    });
  },

  async adjustPoints(uid: string, amount: number, reason: string) {
    return request<{ user: AdminUserRecord }>(
      `/admin/users/${uid}/points-adjustments`,
      {
        method: "POST",
        body: JSON.stringify({ amount, reason }),
      },
    );
  },

  async addMember(uid: string, member: AdminMember) {
    return request<{ user: AdminUserRecord }>(`/admin/users/${uid}/members`, {
      method: "POST",
      body: JSON.stringify({ member }),
    });
  },

  async updateMember(
    uid: string,
    memberId: string,
    changes: Partial<AdminMember>,
    reason?: string,
  ) {
    return request<{ user: AdminUserRecord }>(
      `/admin/users/${uid}/members/${memberId}`,
      {
        method: "PATCH",
        body: JSON.stringify({ changes, reason }),
      },
    );
  },

  async removeMember(uid: string, memberId: string, reason?: string) {
    return request<{ user: AdminUserRecord }>(
      `/admin/users/${uid}/members/${memberId}`,
      {
        method: "DELETE",
        body: JSON.stringify({ reason }),
      },
    );
  },

  async updateAddress(
    uid: string,
    addressId: string,
    changes: Partial<AdminAddress>,
    reason?: string,
  ) {
    return request<{ user: AdminUserRecord }>(
      `/admin/users/${uid}/addresses/${addressId}`,
      {
        method: "PATCH",
        body: JSON.stringify({ changes, reason }),
      },
    );
  },

  async removeAddress(uid: string, addressId: string, reason?: string) {
    return request<{ user: AdminUserRecord }>(
      `/admin/users/${uid}/addresses/${addressId}`,
      {
        method: "DELETE",
        body: JSON.stringify({ reason }),
      },
    );
  },

  async addWarehouse(
    uid: string,
    warehouse: Partial<AdminWarehouse> = {},
    reason?: string,
  ) {
    return request<{
      user: AdminUserRecord;
    }>(`/admin/users/${uid}/warehouses`, {
      method: "POST",
      body: JSON.stringify({
        warehouse,
        reason,
      }),
    });
  },

  async updateWarehouse(
    uid: string,
    warehouseId: string,
    changes: Partial<AdminWarehouse>,
    reason?: string,
  ) {
    return request<{ user: AdminUserRecord }>(
      `/admin/users/${uid}/warehouses/${warehouseId}`,
      {
        method: "PATCH",
        body: JSON.stringify({ changes, reason }),
      },
    );
  },

  async removeWarehouse(uid: string, warehouseId: string, reason?: string) {
    return request<{ user: AdminUserRecord }>(
      `/admin/users/${uid}/warehouses/${warehouseId}`,
      {
        method: "DELETE",
        body: JSON.stringify({ reason }),
      },
    );
  },

  async createPasswordResetLink(uid: string) {
    return request<{ resetLink: string }>(
      `/admin/users/${uid}/password-reset-link`,
      { method: "POST" },
    );
  },

  async getAuditLogs(uid: string) {
    return request<{ logs: AdminAuditRecord[] }>(
      `/admin/users/${uid}/audit-logs`,
    );
  },

  async deleteUser(uid: string, reason: string, permanent = false) {
    return request<{ success: boolean }>(`/admin/users/${uid}`, {
      method: "DELETE",
      body: JSON.stringify({ reason, permanent }),
    });
  },
};
